import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo } from "react";
import { useAuth } from "@/context/auth";
import { fetchClient } from "@/lib/api-client";

export type CartLookupEntry = { id: string; quantity: number };

const CART_LOOKUP_LIMIT = 100;

/**
 * Builds a `productId -> { id, quantity }` lookup map for "already in cart"
 * UI (badges/quantity/add-remove state) shown on every `ProductCard`.
 *
 * `GET /cart` is fully paginated. This map must reflect the *entire* cart,
 * not just the first page — a silently-truncated map would make products
 * past page 1 render as "not in cart" even though they are, which is a
 * wrong-state bug rather than a missing-UI one. There is no scroll surface
 * backing this hook (it's a background lookup, not a rendered list), so
 * pages are fetched eagerly via `useEffect` instead of gating on a scroll
 * sentinel — correctness is needed as soon as possible, not deferred until
 * the user scrolls somewhere.
 */
const useCartCategoryLookup = (isProduct: boolean, enabled: boolean) => {
  const query = useInfiniteQuery({
    queryKey: ["get", "/cart", "lookup", { is_product: isProduct }],
    queryFn: async ({ pageParam }) => {
      const response = await fetchClient.GET("/cart", {
        params: {
          query: {
            page: pageParam,
            limit: CART_LOOKUP_LIMIT,
            is_product: isProduct,
          },
        },
      });
      const error = response.error;
      if (error) throw error;
      return response.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const pagination = lastPage?.data?.pagination;
      if (pagination?.hasNextPage) return pagination.page + 1;
      return undefined;
    },
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const { hasNextPage, isFetching, isFetchNextPageError, fetchNextPage } =
    query;

  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isFetchNextPageError) {
      fetchNextPage();
    }
  }, [enabled, hasNextPage, isFetching, isFetchNextPageError, fetchNextPage]);

  return query;
};

export const useCartLookup = () => {
  const { isLoggedIn } = useAuth();

  const productsQuery = useCartCategoryLookup(true, isLoggedIn);
  const servicesQuery = useCartCategoryLookup(false, isLoggedIn);

  const map = useMemo(() => {
    const m = new Map<string, CartLookupEntry>();
    for (const item of [
      ...(productsQuery.data?.pages.flatMap(
        (page) => page?.data?.result ?? [],
      ) ?? []),
      ...(servicesQuery.data?.pages.flatMap(
        (page) => page?.data?.result ?? [],
      ) ?? []),
    ]) {
      m.set(item.product_id, { id: item.id, quantity: item.quantity });
    }
    return m;
  }, [productsQuery.data, servicesQuery.data]);

  const refetch = useCallback(() => {
    productsQuery.refetch();
    servicesQuery.refetch();
  }, [productsQuery.refetch, servicesQuery.refetch]);

  const isLoading = productsQuery.isLoading || servicesQuery.isLoading;
  const isFetching = productsQuery.isFetching || servicesQuery.isFetching;

  return { map, refetch, isLoading, isFetching };
};
